import { Add, CheckCircle, Close, Info, Psychology, Refresh } from '@mui/icons-material';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    LinearProgress,
    Paper,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    EAnalysisStatus,
    type IExtractedSkill,
    type ISkillExtractionUIData,
} from '@sharedInterfaces/IEmployeeTalkState';
import type { ISkillExtractionComponentProperties } from '@sharedInterfaces/IEmployeeTalkTemplate';
// import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import RowElement from '@src/App/Components/General/FormularElements/RowElement/RowElement';
import type { AppState } from '@store/store';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { ITemplateComponentProps } from '../TemplateEditor/types';
import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';
import { useEmployeeTalk } from './ETTEmployeeTalkProvider';

const ETTSkillExtractionComponent: React.FC<ITemplateComponentProps> = (props) => {
    const theme = useTheme();
    // const lang = useSelector((state: AppState) => state.employee.language);
    // const langStrings = getLanguageTexts(lang).employeeTalk;
    const allSkills = useSelector((state: AppState) => state.company.allSkills);
    const levelDefinitions = useSelector((state: AppState) => state.levelDefinition);

    const properties = props.component.properties as ISkillExtractionComponentProperties;

    // Use ETTEmployeeTalkProvider for CV analysis data and target employee skills
    const {
        secureState,
        selectSkill,
        updateSkillLevel,
        deselectSkill,
        targetEmployeeSkills,
        regenerateSkills,
        isReadOnlyForCurrentUser,
        isManualMode,
    } = useEmployeeTalk();

    const cvAnalysis = {
        state: {
            isAnalyzing: secureState?.analysis.skills.status === EAnalysisStatus.LOADING,
            results: {
                extractedSkills: secureState?.analysis.skills.data || [],
                extractedRoles: secureState?.analysis.roles.data || [],
                extractedCertificates: secureState?.analysis.certificates.data || [],
                generatedDescription: secureState?.analysis.description.data || '',
            },
            error:
                secureState?.analysis.skills.status === EAnalysisStatus.ERROR ? 'CV-Analyse fehlgeschlagen' : undefined,
        },
        retryAnalysis: () => {
            console.log('Retry analysis - TODO: implement with events');
        },
    };

    // Skill levels from Redux with color mapping
    const SKILL_LEVELS = levelDefinitions.map((levelDef, index) => ({
        level: levelDef.level,
        name: levelDef.title,
        description: levelDef.description,
        helperText: levelDef.helperText,
        color:
            index === 0
                ? alpha(theme.palette.info.main, 0.2)
                : index === 1
                  ? alpha(theme.palette.success.main, 0.3)
                  : index === 2
                    ? alpha(theme.palette.warning.main, 0.4)
                    : alpha(theme.palette.error.main, 0.5),
    }));

    const getSkillLevelInfo = (level: number) => SKILL_LEVELS.find((l) => l.level === level) || SKILL_LEVELS[0];
    const getTextColor = (level: number) => (level >= 3 ? 'white' : 'text.primary');

    const [acceptedItems, setAcceptedItems] = useState<{
        skills: Array<{ title: string; level: number; category: string; selected: boolean; id?: number }>;
        roles: string[];
        certificates: string[];
    }>({ skills: [], roles: [], certificates: [] });

    const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [expandedSections, setExpandedSections] = useState({ skills: false });
    const [loading, setLoading] = useState({ skills: false });
    const [errors, setErrors] = useState({ skills: '' });
    const [inlineForm, setInlineForm] = useState({ skill: { name: '', level: 1 as number } });
    const [isRegenerating, setIsRegenerating] = useState<boolean>(false);

    // Sync acceptedItems with backend state (persistent across state updates)
    useEffect(() => {
        // Use provider to get component data (handles dynamic ID internally)
        const componentData = props.value as ISkillExtractionUIData | undefined;

        if (componentData?.skills) {
            setAcceptedItems((prev) => ({
                ...prev,
                skills: componentData.skills,
            }));
        }
    }, [props.value]);

    // REMOVED: Auto-save on every state change causes infinite loop
    // Only save on explicit user actions (handleSkillLevelChange, handleAcceptSkill, etc.)

    // Confidence color helper - for future use when confidence is available
    // const getConfidenceColor = (confidence: number) => {
    //   if (confidence >= 0.8) return 'success';
    //   if (confidence >= 0.6) return 'warning';
    //   return 'error';
    // };

    const renderEditContent = () => (
        <>
            <RowElement title="Manuelle Skills erlauben">
                <input
                    type="checkbox"
                    checked={properties.allowManualSkills}
                    onChange={(e) =>
                        props.onEdit?.({
                            ...props.component,
                            properties: {
                                ...properties,
                                allowManualSkills: e.target.checked,
                            },
                        })
                    }
                />
            </RowElement>
            <RowElement title="Level-Bewertung erforderlich">
                <input
                    type="checkbox"
                    checked={properties.requireLevelRating}
                    onChange={(e) =>
                        props.onEdit?.({
                            ...props.component,
                            properties: {
                                ...properties,
                                requireLevelRating: e.target.checked,
                            },
                        })
                    }
                />
            </RowElement>
            <RowElement title="Label und Beschreibung ausblenden">
                <input
                    type="checkbox"
                    checked={properties.hideLabels ?? false}
                    onChange={(e) =>
                        props.onEdit?.({
                            ...props.component,
                            properties: {
                                ...properties,
                                hideLabels: e.target.checked,
                            },
                        })
                    }
                />
            </RowElement>
        </>
    );

    const handleAcceptSkill = async (skill: IExtractedSkill) => {
        // Optimistic update
        const previousState = acceptedItems.skills;
        setAcceptedItems((prev) => ({
            ...prev,
            skills: prev.skills.map((s) => (s.title === skill.title ? { ...s, selected: true } : s)),
        }));

        try {
            await selectSkill(skill.title);
        } catch (error) {
            // Rollback on error
            setAcceptedItems((prev) => ({ ...prev, skills: previousState }));
            console.error('Failed to accept skill:', error);
        }
    };

    const handleDeselectSkill = async (skillTitle: string) => {
        // Optimistic update
        const previousState = acceptedItems.skills;
        setAcceptedItems((prev) => ({
            ...prev,
            skills: prev.skills.map((s) => (s.title === skillTitle ? { ...s, selected: false } : s)),
        }));

        try {
            await deselectSkill(skillTitle);
        } catch (error) {
            // Rollback on error
            setAcceptedItems((prev) => ({ ...prev, skills: previousState }));
            console.error('Failed to deselect skill:', error);
        }
    };

    const handleSkillLevelChange = async (skillTitle: string, newLevel: number) => {
        // Optimistic update
        const previousState = acceptedItems.skills;
        setAcceptedItems((prev) => ({
            ...prev,
            skills: prev.skills.map((s) => (s.title === skillTitle ? { ...s, level: newLevel } : s)),
        }));

        try {
            await updateSkillLevel(skillTitle, newLevel);
        } catch (error) {
            // Rollback on error
            setAcceptedItems((prev) => ({ ...prev, skills: previousState }));
            console.error('Failed to update skill level:', error);
        }
    };

    const handleInfoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setInfoAnchorEl(event.currentTarget);
    };

    const handleInfoClose = () => {
        setInfoAnchorEl(null);
    };

    const toggleExpansion = () => {
        setExpandedSections((prev) => ({ ...prev, skills: !prev.skills }));
        setErrors({ skills: '' });
    };

    const handleAddSkill = async () => {
        const skillData = inlineForm.skill;
        if (!skillData.name.trim()) {
            setErrors({ skills: 'Bitte wählen Sie eine Fähigkeit aus' });
            return;
        }
        setLoading({ skills: true });

        try {
            // Use event system to select skill with level (backend handles everything in one go)
            await selectSkill(skillData.name, skillData.level);
            // State update comes from backend via WebSocket

            setInlineForm({ skill: { name: '', level: 1 } });
            setExpandedSections({ skills: false });
        } catch (error) {
            setErrors({ skills: 'Fehler beim Hinzufügen der Fähigkeit' });
            console.error('Failed to add skill:', error);
        }
        setLoading({ skills: false });
    };

    const renderAnalysisResults = () => {
        const { results } = cvAnalysis.state;
        if (!results) return null;

        // Separate CV-suggested skills from manually added skills
        const suggestedSkillTitles = new Set((results.extractedSkills || []).map((s) => s.title));
        const manuallyAddedSkills = acceptedItems.skills.filter((skill) => !suggestedSkillTitles.has(skill.title));

        // Group CV-suggested skills by category
        const groupedSkills = (results.extractedSkills || []).reduce(
            (acc, skill) => {
                const category = 'Hard Skills'; // Default, could be enhanced
                if (!acc[category]) acc[category] = [];
                acc[category].push(skill);
                return acc;
            },
            {} as Record<string, IExtractedSkill[]>,
        );

        const infoOpen = Boolean(infoAnchorEl);

        // Filter out all skills (suggested + manually added + target employee's existing) from dropdown
        const manualSkillTitles = manuallyAddedSkills.map((s) => s.title);
        const targetEmployeeSkillTitles = new Set(targetEmployeeSkills.map((s) => s.title));
        const availableSkills = allSkills.filter(
            (skill) =>
                !suggestedSkillTitles.has(skill.title) &&
                !manualSkillTitles.includes(skill.title) &&
                !targetEmployeeSkillTitles.has(skill.title),
        );

        return (
            <Card sx={{ mb: 3 }}>
                <CardHeader
                    title={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                                sx={{
                                    width: 32,
                                    height: 32,
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                }}
                            >
                                2
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 400 }}>
                                Erkannte Skills
                            </Typography>
                            <IconButton size="small" sx={{ ml: 1 }} onClick={handleInfoClick}>
                                <Info fontSize="small" />
                            </IconButton>
                            <Popover
                                open={infoOpen}
                                anchorEl={infoAnchorEl}
                                onClose={handleInfoClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            >
                                <Box sx={{ p: 2, maxWidth: 300 }}>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        Du kannst Fähigkeiten zuweisen und eine Selbsteinschätzung durchführen, die zu
                                        einem Level von {SKILL_LEVELS[0]?.name || 'Junior'} bis{' '}
                                        {SKILL_LEVELS[SKILL_LEVELS.length - 1]?.name || 'Expert'} führt.
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ display: 'block', mb: 1, fontWeight: 600 }}
                                        >
                                            Skill-Level Bewertung:
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.9 }}>
                                            {SKILL_LEVELS.map((level) => (
                                                <Box
                                                    key={level.level}
                                                    sx={{
                                                        flex: '1 1 45%',
                                                        p: 1,
                                                        borderRadius: 0.5,
                                                        backgroundColor: level.color,
                                                        color: getTextColor(level.level),
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ fontSize: '0.8rem', fontWeight: 400 }}
                                                    >
                                                        {level.name}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </Popover>
                        </Box>
                    }
                    sx={{ borderBottom: `1px solid ${theme.palette.divider}`, py: 2 }}
                />
                <CardContent>
                    <Alert
                        severity="info"
                        icon={<Info sx={{ color: theme.palette.info.main }} />}
                        sx={{
                            mb: 2,
                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                            border: `1px solid ${theme.palette.info.main}`,
                            '& .MuiAlert-message': { color: theme.palette.info.main },
                        }}
                    >
                        <Typography variant="body1" sx={{ fontSize: '0.9rem', color: theme.palette.info.main }}>
                            Wähle mindestens eine Fähigkeit aus und bewerte dein Level. Du kannst weitere Fähigkeiten
                            hinzufügen.
                        </Typography>
                    </Alert>

                    {Object.entries(groupedSkills).map(([category, categorySkills]) => (
                        <Box key={category} sx={{ mb: 3 }}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ color: 'text.primary', fontWeight: 500, mb: 1.5, fontSize: '1.1rem' }}
                            >
                                {category}
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Skill
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Level
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Anpassen
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Aktion
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {categorySkills.map((skill) => {
                                            const acceptedSkill = acceptedItems.skills.find(
                                                (s) => s.title === skill.title,
                                            );
                                            const isAccepted = acceptedSkill?.selected === true;
                                            const currentLevel = acceptedSkill?.level || skill.level || 1;
                                            const levelInfo = getSkillLevelInfo(currentLevel);
                                            return (
                                                <TableRow
                                                    key={skill.title}
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2">{skill.title}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={levelInfo.name}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: levelInfo.color,
                                                                color: getTextColor(currentLevel),
                                                                fontWeight: 400,
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <ButtonGroup size="small" variant="outlined">
                                                            {SKILL_LEVELS.map((level) => (
                                                                <Button
                                                                    key={level.level}
                                                                    onClick={() =>
                                                                        handleSkillLevelChange(skill.title, level.level)
                                                                    }
                                                                    variant={
                                                                        currentLevel === level.level
                                                                            ? 'contained'
                                                                            : 'outlined'
                                                                    }
                                                                    disabled={isReadOnlyForCurrentUser}
                                                                    sx={{
                                                                        minWidth: 32,
                                                                        px: 1,
                                                                        backgroundColor:
                                                                            currentLevel === level.level
                                                                                ? level.color
                                                                                : undefined,
                                                                        color:
                                                                            currentLevel === level.level
                                                                                ? getTextColor(level.level)
                                                                                : 'text.primary',
                                                                        '&:hover': {
                                                                            backgroundColor: level.color,
                                                                            color: getTextColor(level.level),
                                                                        },
                                                                    }}
                                                                >
                                                                    {level.level}
                                                                </Button>
                                                            ))}
                                                        </ButtonGroup>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box
                                                            sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}
                                                        >
                                                            {!isAccepted && (
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    disabled={isReadOnlyForCurrentUser}
                                                                    sx={{
                                                                        color: '#0BB140',
                                                                        borderColor: '#0BB140',
                                                                        '&:hover': {
                                                                            backgroundColor: '#0BB140',
                                                                            color: 'white',
                                                                        },
                                                                    }}
                                                                    onClick={() => handleAcceptSkill(skill)}
                                                                >
                                                                    ✓
                                                                </Button>
                                                            )}
                                                            {isAccepted && (
                                                                <Chip
                                                                    icon={<CheckCircle />}
                                                                    label="✓"
                                                                    size="small"
                                                                    onClick={
                                                                        isReadOnlyForCurrentUser
                                                                            ? undefined
                                                                            : () => handleDeselectSkill(skill.title)
                                                                    }
                                                                    sx={{
                                                                        backgroundColor: '#0BB140',
                                                                        color: 'white',
                                                                        cursor: isReadOnlyForCurrentUser
                                                                            ? 'default'
                                                                            : 'pointer',
                                                                        '&:hover': isReadOnlyForCurrentUser
                                                                            ? {}
                                                                            : { backgroundColor: '#0a9e38' },
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ))}

                    {/* Manually Added Skills Section */}
                    {manuallyAddedSkills.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ color: 'text.primary', fontWeight: 500, mb: 1.5, fontSize: '1.1rem' }}
                            >
                                Manuell hinzugefügt
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.secondary.main,
                                                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.secondary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Skill
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.secondary.main,
                                                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.secondary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Level
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.secondary.main,
                                                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.secondary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Anpassen
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.secondary.main,
                                                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.secondary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Aktion
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {manuallyAddedSkills.map((skill) => {
                                            const currentLevel = skill.level || 1;
                                            const levelInfo = getSkillLevelInfo(currentLevel);
                                            const isAccepted = skill.selected === true;
                                            return (
                                                <TableRow
                                                    key={skill.title}
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2">{skill.title}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={levelInfo.name}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: levelInfo.color,
                                                                color: getTextColor(currentLevel),
                                                                fontWeight: 400,
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <ButtonGroup size="small" variant="outlined">
                                                            {SKILL_LEVELS.map((level) => (
                                                                <Button
                                                                    key={level.level}
                                                                    onClick={() =>
                                                                        handleSkillLevelChange(skill.title, level.level)
                                                                    }
                                                                    variant={
                                                                        currentLevel === level.level
                                                                            ? 'contained'
                                                                            : 'outlined'
                                                                    }
                                                                    disabled={isReadOnlyForCurrentUser}
                                                                    sx={{
                                                                        minWidth: 32,
                                                                        px: 1,
                                                                        backgroundColor:
                                                                            currentLevel === level.level
                                                                                ? level.color
                                                                                : undefined,
                                                                        color:
                                                                            currentLevel === level.level
                                                                                ? getTextColor(level.level)
                                                                                : 'text.primary',
                                                                        '&:hover': {
                                                                            backgroundColor: level.color,
                                                                            color: getTextColor(level.level),
                                                                        },
                                                                    }}
                                                                >
                                                                    {level.level}
                                                                </Button>
                                                            ))}
                                                        </ButtonGroup>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box
                                                            sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}
                                                        >
                                                            {!isAccepted && (
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    disabled={isReadOnlyForCurrentUser}
                                                                    sx={{
                                                                        color: '#0BB140',
                                                                        borderColor: '#0BB140',
                                                                        '&:hover': {
                                                                            backgroundColor: '#0BB140',
                                                                            color: 'white',
                                                                        },
                                                                    }}
                                                                    onClick={async () => {
                                                                        const previousState = acceptedItems.skills;
                                                                        setAcceptedItems((prev) => ({
                                                                            ...prev,
                                                                            skills: prev.skills.map((s) =>
                                                                                s.title === skill.title
                                                                                    ? { ...s, selected: true }
                                                                                    : s,
                                                                            ),
                                                                        }));
                                                                        try {
                                                                            await selectSkill(skill.title, skill.level);
                                                                        } catch (error) {
                                                                            setAcceptedItems((prev) => ({
                                                                                ...prev,
                                                                                skills: previousState,
                                                                            }));
                                                                            console.error(
                                                                                'Failed to reselect skill:',
                                                                                error,
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    ✓
                                                                </Button>
                                                            )}
                                                            {isAccepted && (
                                                                <Chip
                                                                    icon={<CheckCircle />}
                                                                    label="✓"
                                                                    size="small"
                                                                    onClick={
                                                                        isReadOnlyForCurrentUser
                                                                            ? undefined
                                                                            : () => handleDeselectSkill(skill.title)
                                                                    }
                                                                    sx={{
                                                                        backgroundColor: '#0BB140',
                                                                        color: 'white',
                                                                        cursor: isReadOnlyForCurrentUser
                                                                            ? 'default'
                                                                            : 'pointer',
                                                                        '&:hover': isReadOnlyForCurrentUser
                                                                            ? {}
                                                                            : { backgroundColor: '#0a9e38' },
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {/* Inline skill addition with Autocomplete */}
                    <Collapse in={expandedSections.skills}>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Neue Fähigkeit hinzufügen
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Autocomplete
                                    freeSolo
                                    options={availableSkills.map((skill) => skill.title)}
                                    value={inlineForm.skill.name}
                                    onChange={(_, newValue) => {
                                        setInlineForm((prev) => ({
                                            ...prev,
                                            skill: { ...prev.skill, name: newValue || '' },
                                        }));
                                        setErrors({ skills: '' });
                                    }}
                                    onInputChange={(_, newInputValue) => {
                                        setInlineForm((prev) => ({
                                            ...prev,
                                            skill: { ...prev.skill, name: newInputValue },
                                        }));
                                        setErrors({ skills: '' });
                                    }}
                                    ListboxProps={{
                                        style: { maxHeight: '200px' },
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Fähigkeit auswählen oder eingeben"
                                            size="small"
                                            error={!!errors.skills}
                                            helperText={errors.skills}
                                        />
                                    )}
                                />
                                <Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mb: 0.5 }}
                                    >
                                        Level auswählen:
                                    </Typography>
                                    <ButtonGroup size="small" variant="outlined" fullWidth>
                                        {SKILL_LEVELS.map((level) => (
                                            <Button
                                                key={level.level}
                                                onClick={() =>
                                                    setInlineForm((prev) => ({
                                                        ...prev,
                                                        skill: { ...prev.skill, level: level.level },
                                                    }))
                                                }
                                                variant={
                                                    inlineForm.skill.level === level.level ? 'contained' : 'outlined'
                                                }
                                                sx={{
                                                    backgroundColor:
                                                        inlineForm.skill.level === level.level
                                                            ? level.color
                                                            : undefined,
                                                    color:
                                                        inlineForm.skill.level === level.level
                                                            ? getTextColor(level.level)
                                                            : 'text.primary',
                                                    '&:hover': {
                                                        backgroundColor: level.color,
                                                        color: getTextColor(level.level),
                                                    },
                                                }}
                                            >
                                                {level.name}
                                            </Button>
                                        ))}
                                    </ButtonGroup>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleAddSkill}
                                        disabled={!inlineForm.skill.name || loading.skills}
                                        startIcon={loading.skills ? <CircularProgress size={16} /> : null}
                                    >
                                        {loading.skills ? 'Hinzufügen...' : 'Hinzufügen'}
                                    </Button>
                                    <Button variant="outlined" size="small" onClick={toggleExpansion}>
                                        Abbrechen
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Collapse>

                    {!isReadOnlyForCurrentUser && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            {!isManualMode && (secureState?.analysis.skills.status === EAnalysisStatus.COMPLETED || secureState?.analysis.skills.status === EAnalysisStatus.ERROR) && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={
                                        isRegenerating || cvAnalysis.state.isAnalyzing ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <Refresh />
                                        )
                                    }
                                    onClick={async () => {
                                        setIsRegenerating(true);
                                        try {
                                            await regenerateSkills();
                                        } catch (error) {
                                            console.error('Failed to regenerate skills:', error);
                                        } finally {
                                            setTimeout(() => setIsRegenerating(false), 5000);
                                        }
                                    }}
                                    disabled={cvAnalysis.state.isAnalyzing || isRegenerating}
                                    sx={{
                                        color: theme.palette.primary.main,
                                        borderColor: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.main,
                                            color: 'white',
                                        },
                                    }}
                                >
                                    {isRegenerating || cvAnalysis.state.isAnalyzing
                                        ? 'Generiere...'
                                        : 'Skills neu analysieren'}
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={expandedSections.skills ? <Close /> : <Add />}
                                onClick={toggleExpansion}
                                sx={{
                                    color: theme.palette.primary.main,
                                    borderColor: theme.palette.primary.main,
                                    '&:hover': { backgroundColor: theme.palette.primary.main, color: 'white' },
                                }}
                            >
                                {expandedSections.skills ? 'Schließen' : 'Hinzufügen'}
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    const renderViewContent = () => {
        const { state } = cvAnalysis;

        if (state.isAnalyzing) {
            return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Psychology sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Analysiere Ihren Lebenslauf...
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Bitte warten Sie, während wir Ihre Skills und Erfahrungen extrahieren.
                    </Typography>
                    <LinearProgress sx={{ maxWidth: 300, mx: 'auto' }} />
                </Box>
            );
        }

        if (state.error) {
            return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        <Typography variant="body1" gutterBottom>
                            Fehler bei der CV-Analyse
                        </Typography>
                        <Typography variant="body2">{state.error}</Typography>
                    </Alert>
                    <Button variant="outlined" startIcon={<Refresh />} onClick={cvAnalysis.retryAnalysis}>
                        Erneut versuchen
                    </Button>
                </Box>
            );
        }

        if (!state.results) {
            return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Psychology sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom color="text.secondary">
                        Keine CV-Analyse verfügbar
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Laden Sie zuerst einen Lebenslauf hoch, um die Analyse zu starten.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ width: '100%' }}>
                {renderAnalysisResults()}

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                    Wählen Sie Skills aus der Tabelle und passen Sie die Level-Bewertung an. Weitere Skills können über
                    den "Hinzufügen"-Button ergänzt werden.
                </Typography>
            </Box>
        );
    };

    return (
        <ETTBaseTemplateComponent
            {...props}
            renderEditContent={renderEditContent}
            renderViewContent={renderViewContent}
            hideLabels={properties.hideLabels}
        />
    );
};

export default ETTSkillExtractionComponent;
